import type { PushPermission } from '@/lib/push/pushPermission'

/**
 * How long after a "Not now" before the prompt may reappear.
 *
 * CHOSEN, NOT MEASURED: there is no data behind 7 days. It is the one policy
 * number here whose effect is bounded — being wrong only shifts when the card
 * returns, never whether it can.
 */
export const PUSH_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Delay between the app settling and the first reveal, so the card does not
 * compete with the initial route load. Also CHOSEN, not measured.
 */
export const PUSH_PROMPT_SHOW_DELAY_MS = 8_000

/**
 * There is deliberately NO lifetime cap on snoozes. A cap is a permanent
 * cutoff, and any number we picked would be unmeasured — the failure mode of a
 * wrong cap is that a user who snoozed N times can never be offered push again,
 * even years later, with no way back except finding the settings screen. The
 * cooldown plus an explicit "Don't ask again" control cover the same ground
 * while leaving that door open. Add a cap only with a measurement behind it.
 */

/**
 * Per-user, persisted prompt policy.
 *
 * There is no snooze COUNTER here. An earlier draft carried one, but with the
 * lifetime cap gone nothing read it — a persisted, incremented, asserted field
 * that changed no behaviour, which is worse than absent because tests over it
 * look like coverage.
 */
export type PushPromptPolicy = {
  lastSnoozedAt: number | null
  /** Explicit user choice: "Don't ask again", or pressing Disable in settings. */
  dismissedForever: boolean
}

export const EMPTY_PUSH_PROMPT_POLICY: PushPromptPolicy = {
  lastSnoozedAt: null,
  dismissedForever: false,
}

export type PushPromptInput = {
  /** Whether an authenticated user is loaded (not merely a valid session). */
  hasUser: boolean
  isOnline: boolean
  /** `isPushRegisteredOnThisDevice(userId)` — THIS user already registered here. */
  isRegisteredOnThisDevice: boolean
  permission: PushPermission
  policy: PushPromptPolicy
  /** An Enable attempt already failed this session for a non-user reason. */
  isSessionSuppressed: boolean
  /** A user-initiated offline prefetch is running; don't stack UI on it. */
  isPrefetchActive: boolean
  /**
   * A request dominated by server work is in flight (`useServerWorkStore`) —
   * today a synchronous report PDF render, which runs for tens of seconds while
   * the user watches a spinner. Revealing an offer then interrupts the one thing
   * they are waiting for, and pressing Enable would queue POST /push_devices
   * behind that render (2026-10-16: it timed out behind a 44 s render holding
   * the PHP session lock and surfaced as a failed-push toast).
   */
  isServerWorkInFlight: boolean
  /** On /settings/notifications the page's own control is the better affordance. */
  isOnNotificationSettingsRoute: boolean
  /**
   * Some dialog, sheet or popover is open (`isAnyDialogOpen()`). The card itself
   * never counts: it is a `role="region"`, not a dialog, and installs no layer.
   */
  isAnotherDialogOpen: boolean
  now: number
}

export type PushPromptSkipReason =
  | 'no-user'
  | 'offline'
  | 'already-registered'
  | 'permission-denied'
  | 'unsupported'
  | 'dismissed-forever'
  | 'session-suppressed'
  | 'within-cooldown'
  | 'prefetch-active'
  | 'server-work-in-flight'
  | 'on-settings-route'
  | 'dialog-open'

export type PushPromptDecision = { show: true } | { show: false; reason: PushPromptSkipReason }

/**
 * Whether the soft prompt may be revealed. Pure on purpose: every branch is
 * reachable from explicit inputs, so the policy can be pinned without mounting
 * a component or faking a platform.
 *
 * `granted` counts as askable. Two real states produce granted-but-unregistered
 * — Android < 13 (where the plugin hard-returns granted; see pushPermission.ts)
 * and any device that logged out or lost its Firebase registration while the OS
 * grant survived. Treating granted as "already handled" would make the prompt
 * unreachable for all of them. Pressing Enable in that state surfaces no OS
 * dialog; it just completes registration.
 */
export function decidePushPrompt(input: PushPromptInput): PushPromptDecision {
  if (!input.hasUser) return { show: false, reason: 'no-user' }
  // Registration POSTs /push_devices, so offline the Enable button could only fail.
  if (!input.isOnline) return { show: false, reason: 'offline' }
  if (input.isRegisteredOnThisDevice) return { show: false, reason: 'already-registered' }

  // A denial cannot be undone from inside the app (web blocks the origin, iOS
  // requires system Settings), so asking again would be a dead end. The
  // permission state itself is the suppression — no persisted flag needed.
  if (input.permission === 'denied') return { show: false, reason: 'permission-denied' }
  if (input.permission === 'unsupported') return { show: false, reason: 'unsupported' }

  if (input.policy.dismissedForever) return { show: false, reason: 'dismissed-forever' }
  if (input.isSessionSuppressed) return { show: false, reason: 'session-suppressed' }

  if (
    input.policy.lastSnoozedAt !== null &&
    input.now - input.policy.lastSnoozedAt < PUSH_PROMPT_COOLDOWN_MS
  ) {
    return { show: false, reason: 'within-cooldown' }
  }

  if (input.isPrefetchActive) return { show: false, reason: 'prefetch-active' }
  if (input.isServerWorkInFlight) return { show: false, reason: 'server-work-in-flight' }
  if (input.isOnNotificationSettingsRoute) return { show: false, reason: 'on-settings-route' }

  // LAST on purpose. An open dialog only postpones the reveal; it never makes the
  // offer wrong. Checked any earlier, it would mask a reason the gate RETRACTS a
  // visible card for — navigating to notification settings with a dialog open
  // would then report 'dialog-open' and leave the card standing.
  //
  // Why it defers at all: on staging (2026-09-25) the card opened on top of an
  // already-open "Dodaj lokalizację" dialog. From the code, not measured: the
  // card is `fixed` at z-95 outside the dialog's portal — above the dialog
  // overlay (z-50), below its content wrapper (z-[1050]) — and Radix's body
  // `pointer-events: none` guard leaves every button outside that portal
  // unpressable.
  if (input.isAnotherDialogOpen) return { show: false, reason: 'dialog-open' }

  return { show: true }
}
